import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { openIntake, openQuestionnaire, openEbook } from "@/lib/ebook";

const STEPS = [
  { n: "01", title: "Discover Crowned", body: "Learn about the Crowned experiences and decide what type of masterpiece you want to create." },
  { n: "02", title: "Need help choosing?", body: "Complete the Crowned Intake — a short questionnaire that helps us recommend the right experience.", cta: { label: "Start My Intake", onClick: () => openIntake("how-it-works") } },
  { n: "03", title: "Choose your experience", body: "Royal Essence · Sovereign Canvas · Majestic Vibes." },
  { n: "04", title: "Make your initial payment", body: "Your initial payment officially begins your Crowned experience." },
  { n: "05", title: "Tell us your story", body: "Complete your Crowned Creative Questionnaire so we can build the artwork around your vision.", cta: { label: "Open Questionnaire", onClick: () => openQuestionnaire("how-it-works") } },
  { n: "06", title: "Photograph / creative direction", body: "Royal Essence: photography coordination. Sovereign: submit your chosen photograph. Majestic Vibes: submit inspiration & creative direction." },
  { n: "07", title: "We create", body: "Crowned transforms your story and creative direction into the first artwork proof." },
  { n: "08", title: "Review your masterpiece", body: "Up to two complimentary revision rounds are included." },
  { n: "09", title: "Final approval", body: "You approve the final artwork before we go to print." },
  { n: "10", title: "Complete remaining payment", body: "When applicable, we invoice the remaining balance before production." },
  { n: "11", title: "Production", body: "Approved artwork moves into museum-grade production." },
  { n: "12", title: "Delivery", body: "Your Crowned masterpiece is packaged and shipped to your door." },
];

export default function HowItWorks() {
  return (
    <div className="relative">
      <SEO
        title="How Crowned Canvas Creations Works | Custom Artwork Process"
        description="The 12-step Crowned process — from choosing your experience and initial payment to your creative questionnaire, first proof, two complimentary revisions, production and delivery."
        path="/how-it-works"
      />
      <Header />
      <main className="pt-24 md:pt-32">
        <section className="relative">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_15%_30%,rgba(130,73,55,0.25),transparent_60%)]" aria-hidden />
          <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-14 md:py-20">
            <p className="overline">How It Works</p>
            <h1 className="font-serif-display text-4xl md:text-5xl lg:text-[80px] leading-[1] mt-5 max-w-4xl">
              Twelve steps from<br /><span className="italic text-[#D4AF37]">idea to heirloom.</span>
            </h1>
            <p className="text-white/70 mt-6 max-w-2xl font-light leading-relaxed text-base md:text-lg">
              Every Crowned experience follows the same measured process. Nothing rushed. Nothing generic.
            </p>
          </div>
        </section>

        <section className="pb-24 md:pb-32">
          <div className="max-w-[1100px] mx-auto px-6 md:px-12">
            <ol className="border-t border-white/10">
              {STEPS.map((s) => (
                <li key={s.n} data-testid={`step-${s.n}`} className="grid md:grid-cols-12 gap-6 py-8 md:py-10 border-b border-white/10 hover:bg-[#824937]/8 transition-colors px-2">
                  <div className="md:col-span-2">
                    <span className="font-serif-display text-3xl md:text-4xl text-[#D4AF37]">Step {s.n}</span>
                  </div>
                  <div className="md:col-span-10">
                    <h3 className="font-serif-display text-2xl md:text-3xl leading-tight">{s.title}</h3>
                    <p className="text-white/65 mt-3 font-light leading-relaxed">{s.body}</p>
                    {s.cta && (
                      <button type="button" onClick={s.cta.onClick} data-testid={`step-cta-${s.n}`}
                        className="mt-5 inline-flex items-center gap-2 border border-[#D4AF37]/50 text-[#D4AF37] px-5 py-2.5 text-xs uppercase tracking-[0.22em] hover:bg-[#D4AF37] hover:text-black transition-colors">
                        {s.cta.label}<ArrowUpRight className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ol>
            <div className="mt-14 text-center">
              <Link to="/#experiences" data-testid="how-cta"
                className="inline-flex items-center gap-3 bg-[#D4AF37] text-black px-7 py-4 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#e6c245] transition-all">
                Choose Your Experience<ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
